import type { ComponentPropsWithoutRef, ElementType, ReactNode } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";

import {
  getSafeFeaturePageUrl,
  type FeaturePage,
  type FeaturePageAlignment,
  type FeaturePageSize,
} from "../lib/feature-page";

const markdownComponents: Components = {
  a: ({ children, href }) => {
    const safeHref = getSafeFeaturePageUrl(href);

    return safeHref ? (
      <a className="feature-page__link" href={safeHref}>
        {children}
      </a>
    ) : (
      <span className="feature-page__unsafe-link">{children}</span>
    );
  },
  blockquote: ({ children }) => (
    <blockquote className="feature-page__blockquote">{children}</blockquote>
  ),
  code: ({ children, className }) => (
    <code className={className ? `feature-page__code ${className}` : "feature-page__code"}>
      {children}
    </code>
  ),
  h1: ({ children }) => <h3 className="feature-page__markdown-heading">{children}</h3>,
  h2: ({ children }) => <h3 className="feature-page__markdown-heading">{children}</h3>,
  h3: ({ children }) => <h3 className="feature-page__markdown-heading">{children}</h3>,
  h4: ({ children }) => <h4 className="feature-page__markdown-heading">{children}</h4>,
  h5: ({ children }) => <h5 className="feature-page__markdown-heading">{children}</h5>,
  h6: ({ children }) => <h6 className="feature-page__markdown-heading">{children}</h6>,
  hr: () => <hr className="feature-page__divider" />,
  li: ({ children }) => <li className="feature-page__markdown-list-item">{children}</li>,
  ol: ({ children }) => <ol className="feature-page__markdown-list">{children}</ol>,
  p: ({ children }) => <p className="feature-page__markdown-paragraph">{children}</p>,
  pre: ({ children }) => <pre className="feature-page__preformatted">{children}</pre>,
  table: ({ children }) => (
    <div className="feature-page__table-scroll" tabIndex={0}>
      <table className="feature-page__table">{children}</table>
    </div>
  ),
  tbody: ({ children }) => <tbody className="feature-page__table-body">{children}</tbody>,
  td: ({ children }) => <td className="feature-page__table-cell">{children}</td>,
  th: ({ children }) => <th className="feature-page__table-heading">{children}</th>,
  thead: ({ children }) => <thead className="feature-page__table-head">{children}</thead>,
  tr: ({ children }) => <tr className="feature-page__table-row">{children}</tr>,
  ul: ({ children }) => <ul className="feature-page__markdown-list">{children}</ul>,
  img: () => null,
};

const inlineMarkdownElements = ["p", "strong", "em", "del", "a", "code", "br"];
const nonLinkInlineMarkdownElements = inlineMarkdownElements.filter(
  (element) => element !== "a",
);

interface InlineMarkdownProps {
  children: string;
  as: ElementType;
  className: string;
  allowLinks?: boolean;
}

function InlineMarkdown({
  children,
  as: Wrapper,
  className,
  allowLinks = true,
}: InlineMarkdownProps) {
  const components: Components = {
    ...markdownComponents,
    p: ({ children: markdownChildren }) => <>{markdownChildren}</>,
  };

  return (
    <Wrapper className={className}>
      <ReactMarkdown
        allowedElements={
          allowLinks ? inlineMarkdownElements : nonLinkInlineMarkdownElements
        }
        components={components}
        remarkPlugins={[remarkGfm]}
        skipHtml
        unwrapDisallowed
        urlTransform={(url) => getSafeFeaturePageUrl(url) ?? ""}
      >
        {children}
      </ReactMarkdown>
    </Wrapper>
  );
}

function MarkdownContent({ children }: { children: string }) {
  return (
    <ReactMarkdown
      components={markdownComponents}
      remarkPlugins={[remarkGfm]}
      skipHtml
      urlTransform={(url) => getSafeFeaturePageUrl(url) ?? ""}
    >
      {children}
    </ReactMarkdown>
  );
}

function alignmentClass(alignment: FeaturePageAlignment | undefined) {
  return alignment ? ` feature-page--align-${alignment}` : "";
}

function levelClass(level: number | undefined) {
  if (level === undefined || level <= 1) {
    return "";
  }

  return ` feature-page--level-${Math.min(level, 4)}`;
}

const spacingTokens = [
  [0, "zero"],
  [2, "xxxs"],
  [4, "xxs"],
  [8, "xs"],
  [12, "sm"],
  [16, "md"],
  [24, "lg"],
  [32, "xl"],
  [48, "xxl"],
  [64, "xxxl"],
] as const;

function closestSpacingToken(value: number) {
  const safeValue = Number.isFinite(value) ? Math.max(0, value) : 0;

  return spacingTokens.reduce((closest, token) =>
    Math.abs(token[0] - safeValue) < Math.abs(closest[0] - safeValue)
      ? token
      : closest,
  )[1];
}

function spacerClass(content: string | undefined) {
  return `feature-page__spacer--${closestSpacingToken(Number(content ?? 0))}`;
}

function gridSizeClass(value: number, bound: "minimum" | "maximum") {
  let size = "wide";

  if (value < 160) {
    size = "compact";
  } else if (value < 280) {
    size = "regular";
  }

  return `feature-page__grid--${bound}-${size}`;
}

function joinClassNames(...classNames: Array<string | undefined>) {
  return classNames.filter(Boolean).join(" ");
}

interface FeaturePageNodeProps {
  page: FeaturePage;
}

function FeaturePageNode({ page }: FeaturePageNodeProps): ReactNode {
  const nodeClassName = joinClassNames(
    "feature-page__node",
    alignmentClass(page.alignment).trim(),
    levelClass(page.level).trim(),
  );

  if (page.columns) {
    return (
      <div className={joinClassNames(nodeClassName, "feature-page__columns")}>
        {page.columns.map((child) => (
          <FeaturePageNode key={child.id} page={child} />
        ))}
      </div>
    );
  }

  if (page.rows) {
    return (
      <div className={joinClassNames(nodeClassName, "feature-page__rows")}>
        {page.rows.map((child) => (
          <FeaturePageNode key={child.id} page={child} />
        ))}
      </div>
    );
  }

  if (page.viewType === "grid" && page.grid) {
    const { grid } = page;

    return (
      <div
        className={joinClassNames(
          nodeClassName,
          "feature-page__grid",
          `feature-page__grid--${grid.orientation}`,
          `feature-page__grid--gap-${closestSpacingToken(grid.spacing)}`,
          gridSizeClass(grid.minimumSize, "minimum"),
          gridSizeClass(grid.maximumSize, "maximum"),
        )}
      >
        {grid.content.map((child) => (
          <FeaturePageNode key={child.id} page={child} />
        ))}
      </div>
    );
  }

  if (!page.viewType || page.content === undefined) {
    return null;
  }

  switch (page.viewType) {
    case "title":
      return (
        <InlineMarkdown as="h2" className={joinClassNames(nodeClassName, "feature-page__title")}>
          {page.content}
        </InlineMarkdown>
      );

    case "subtitle":
      return (
        <InlineMarkdown as="h3" className={joinClassNames(nodeClassName, "feature-page__subtitle")}>
          {page.content}
        </InlineMarkdown>
      );

    case "caption":
      return (
        <InlineMarkdown as="p" className={joinClassNames(nodeClassName, "feature-page__caption")}>
          {page.content}
        </InlineMarkdown>
      );

    case "paragraph":
      return (
        <div className={joinClassNames(nodeClassName, "feature-page__paragraph")}>
          <MarkdownContent>{page.content}</MarkdownContent>
        </div>
      );

    case "bulletedParagraph": {
      const items = page.content
        .split("\n")
        .map((item) => item.trim())
        .filter(Boolean);

      return (
        <ul className={joinClassNames(nodeClassName, "feature-page__bulleted-paragraph")}>
          {items.map((item, index) => (
            <li className="feature-page__bullet-item" key={`${page.id}-${index}`}>
              <InlineMarkdown as="span" className="feature-page__bullet-text">
                {item}
              </InlineMarkdown>
            </li>
          ))}
        </ul>
      );
    }

    case "imageUrl": {
      const imageUrl = getSafeFeaturePageUrl(page.content, "image");

      return imageUrl ? (
        <img
          alt=""
          className={joinClassNames(nodeClassName, "feature-page__image")}
          decoding="async"
          loading="lazy"
          src={imageUrl}
        />
      ) : null;
    }

    case "button": {
      const action = getSafeFeaturePageUrl(page.button?.action);

      return action && page.button ? (
        <a
          className={joinClassNames(
            nodeClassName,
            "feature-page__button",
            `feature-page__button--${page.button.style}`,
            `feature-page__button--${sizeClass(page.button.size)}`,
          )}
          href={action}
        >
          <InlineMarkdown
            allowLinks={false}
            as="span"
            className="feature-page__button-label"
          >
            {page.content}
          </InlineMarkdown>
        </a>
      ) : null;
    }

    case "padding":
      return (
        <span
          aria-hidden="true"
          className={joinClassNames(
            nodeClassName,
            "feature-page__spacer",
            spacerClass(page.content),
          )}
        />
      );

    case "grid":
      return null;
  }
}

function sizeClass(size: FeaturePageSize) {
  return size === "extraLarge" ? "extra-large" : size;
}

export interface FeaturePageRendererProps
  extends Omit<ComponentPropsWithoutRef<"div">, "children"> {
  page: FeaturePage;
}

/** Renders the same recursive document tree used by the iOS FeaturePage view. */
export function FeaturePageRenderer({
  page,
  className,
  ...containerProps
}: FeaturePageRendererProps) {
  return (
    <div
      {...containerProps}
      className={joinClassNames("feature-page", className)}
    >
      <FeaturePageNode page={page} />
    </div>
  );
}
